import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { MessageCircle, Send, Loader2, Droplets, Sparkles } from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';

export default function ChatPage() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        // Welcome message
        setMessages([{
            role: 'assistant',
            content: 'Bonjour ! Je suis l\'assistant IA de O.C.A Piscine, expert en entretien de piscine.\n\nJe peux vous aider avec :\n- L\'analyse et l\'équilibrage de l\'eau\n- Les recommandations de produits CTX Pro\n- Les conseils d\'entretien saisonniers\n- La résolution de problèmes (eau verte, trouble...)\n\nPour les problèmes techniques (fuites, pompe, filtration...), je vous orienterai vers nos techniciens experts.\n\nComment puis-je vous aider aujourd\'hui ?'
        }]);
    }, []);

    useEffect(() => {
        // Scroll to bottom when new messages
        // ScrollArea uses a viewport inside, we need to target that
        if (scrollRef.current) {
            const viewport = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
            if (viewport) {
                viewport.scrollTop = viewport.scrollHeight;
            }
        }
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || loading) return;

        const userMessage = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setLoading(true);

        try {
            const token = localStorage.getItem('auth_token');
            const headers = {
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    message: userMessage,
                    session_id: sessionId
                })
            });

            if (!response.ok) {
                throw new Error('Erreur serveur');
            }

            const data = await response.json();
            setSessionId(data.session_id);
            setMessages(prev => [...prev, { role: 'assistant', content: data.response || data.content || 'Réponse vide' }]);
        } catch (error) {
            console.error('Chat error:', error);
            toast.error('Erreur de communication avec l\'assistant');
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                content: 'Désolé, je rencontre un problème technique. Veuillez réessayer ou contactez-nous au 06 51 96 14 74.' 
            }]);
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const suggestedQuestions = [
        'Comment équilibrer le pH de ma piscine ?',
        'Mon eau est verte, que faire ?',
        'Quel dosage de chlore pour 50m³ ?',
        'Comment hiverner ma piscine ?'
    ];

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="chat-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-[calc(100vh-8rem)]">
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="flex items-center justify-center gap-3 mb-2">
                        <MessageCircle className="h-8 w-8 text-primary" />
                        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Assistant IA</h1>
                    </div>
                    <p className="text-muted-foreground">
                        Posez vos questions sur l'entretien de votre piscine
                    </p>
                </div>

                {/* Chat Container */}
                <Card className="glass rounded-3xl h-[calc(100%-120px)] flex flex-col" data-testid="chat-container">
                    {/* Messages */}
                    <ScrollArea className="flex-1 p-6" ref={scrollRef}>
                        <div className="space-y-6">
                            {messages.map((message, index) => (
                                <div
                                    key={index}
                                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    data-testid={`message-${index}`}
                                >
                                    {message.role === 'assistant' && (
                                        <div className="flex-shrink-0 mr-3">
                                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                <Droplets className="h-5 w-5 text-primary" />
                                            </div>
                                        </div>
                                    )}
                                    <div
                                        className={`max-w-[80%] ${
                                            message.role === 'user'
                                                ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-md'
                                                : 'glass rounded-2xl rounded-bl-md'
                                        } px-4 py-3`}
                                    >
                                        <p className="whitespace-pre-wrap text-sm sm:text-base">{message.content}</p>
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="flex justify-start">
                                    <div className="flex-shrink-0 mr-3">
                                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Droplets className="h-5 w-5 text-primary animate-pulse" />
                                        </div>
                                    </div>
                                    <div className="glass rounded-2xl rounded-bl-md px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                            <span className="text-muted-foreground">O.C.A Piscine réfléchit...</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </ScrollArea>

                    {/* Suggested Questions (show only at start) */}
                    {messages.length === 1 && (
                        <div className="px-6 pb-4">
                            <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                                <Sparkles className="h-4 w-4" />
                                Questions suggérées
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {suggestedQuestions.map((question, index) => (
                                    <Button
                                        key={index}
                                        variant="secondary"
                                        size="sm"
                                        className="rounded-full text-xs"
                                        onClick={() => {
                                            setInput(question);
                                            inputRef.current?.focus();
                                        }}
                                        data-testid={`suggestion-${index}`}
                                    >
                                        {question}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Input */}
                    <div className="p-4 border-t border-border">
                        <div className="flex gap-3">
                            <Input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Posez votre question..."
                                className="flex-1 h-12 rounded-full px-5"
                                disabled={loading}
                                data-testid="chat-input"
                            />
                            <Button
                                onClick={handleSend}
                                disabled={!input.trim() || loading}
                                size="icon"
                                className="h-12 w-12 rounded-full"
                                data-testid="send-btn"
                            >
                                <Send className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
